import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Trash2 } from "lucide-react";

const formSchema = z.object({
  brandName: z.string().min(1, "Brand name is required"),
  website: z.string().min(1, "Website is required").refine(
    (url) => {
      // Allow URLs with or without protocol
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      return urlPattern.test(url);
    },
    { message: "Please enter a valid website URL" }
  ),
  contactPersonName: z.string().min(1, "Contact person name is required"),
  contactEmail: z.string().email("Please enter a valid email address"),
  contactPhoneCountryCode: z.string().min(1, "Country code is required"),
  contactPhone: z.string().min(1, "Contact phone number is required"),
  promoCode: z.string().optional(),
  shoppingFeeds: z.array(z.object({
    url: z.string().min(1, "Shopping feed link is required").refine(
      (url) => {
        // Allow URLs with or without protocol
        const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        return urlPattern.test(url);
      },
      { message: "Please enter a valid shopping feed URL" }
    ),
    language: z.string().min(1, "Language is required"),
    country: z.string().min(1, "Country is required"),
  })).min(1, "At least one shopping feed is required"),
  agreementAccepted: z.boolean().refine((val) => val === true, {
    message: "You must agree to the Brand Partnership Agreement",
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface BrandRegistrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BrandRegistrationDialog = ({ open, onOpenChange }: BrandRegistrationDialogProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      brandName: "",
      website: "",
      contactPersonName: "",
      contactEmail: "",
      contactPhoneCountryCode: "US",
      contactPhone: "",
      promoCode: "",
      shoppingFeeds: [{ url: "", language: "", country: "" }],
      agreementAccepted: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "shoppingFeeds",
  });

  const languages = [
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" },
    { value: "it", label: "Italian" },
    { value: "pt", label: "Portuguese" },
    { value: "nl", label: "Dutch" },
    { value: "sv", label: "Swedish" },
    { value: "da", label: "Danish" },
    { value: "no", label: "Norwegian" },
  ];

  const countries = [
    { value: "US", label: "United States" },
    { value: "CA", label: "Canada" },
    { value: "UK", label: "United Kingdom" },
    { value: "DE", label: "Germany" },
    { value: "FR", label: "France" },
    { value: "IT", label: "Italy" },
    { value: "ES", label: "Spain" },
    { value: "NL", label: "Netherlands" },
    { value: "SE", label: "Sweden" },
    { value: "DK", label: "Denmark" },
    { value: "NO", label: "Norway" },
    { value: "AU", label: "Australia" },
    { value: "BR", label: "Brazil" },
    { value: "MX", label: "Mexico" },
  ];

  const extractReferrerDomain = (referrerUrl: string): string => {
    if (!referrerUrl) return 'Direct';
    try {
      const domain = referrerUrl.replace(/^https?:\/\/(www\.)?([^\/]+).*/, '$2');
      if (domain.includes('tiktok.com')) return 'tiktok.com';
      if (domain.includes('facebook.com')) return 'facebook.com';
      if (domain.includes('instagram.com')) return 'instagram.com';
      if (domain.includes('twitter.com') || domain.includes('x.com')) return 'twitter.com';
      if (domain.includes('linkedin.com')) return 'linkedin.com';
      if (domain.includes('youtube.com')) return 'youtube.com';
      if (domain.includes('pinterest.com')) return 'pinterest.com';
      if (domain.includes('reddit.com')) return 'reddit.com';
      if (domain.includes('google.')) return 'google.com';
      return domain;
    } catch {
      return 'Direct';
    }
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const referrerSource = extractReferrerDomain(document.referrer);
      
      // Save to Supabase leads table
      const { error } = await supabase
        .from('leads')
        .insert({
          type: 'brand',
          email: values.contactEmail,
          brand_name: values.brandName,
          website: values.website,
          contact_name: values.contactPersonName,
          contact_email: values.contactEmail,
          contact_phone: `${values.contactPhoneCountryCode}:${values.contactPhone}`,
          promo_code: values.promoCode,
          shopping_feed_link: values.shoppingFeeds[0]?.url || "",
          referrer_source: referrerSource,
          metadata: {
            agreement_accepted: values.agreementAccepted,
            shopping_feeds: values.shoppingFeeds,
            submitted_from: 'brand_registration_dialog'
          }
        });

      if (error) {
        throw error;
      }
      
      toast({
        title: "Registration Submitted",
        description: "Thank you for your interest! We'll be in touch soon.",
      });
      
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Brand registration error:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const agreementText = `Creator Partnership Agreement
Effective upon acceptance by the Creator
By completing the form below and clicking I Agree, you ("Creator", "you", or "your") accept the following terms with Asteroid Ventures FZC, the owner and operator of Cloutfit.ai and OutfitAlliance.com ("Cloutfit", "we", "our", or "us").

1. Program Overview
You are joining our Creator Program to help connect fashion inspiration with commerce. By allowing us to feature your public content, we will generate a custom shoppable page that matches outfits from your posts to products available through our brand partners. You will earn a commission on qualifying sales.

2. Content Usage and Ownership
You grant us permission to:
• Embed and display your public social media content (for example, Instagram posts) using official tools such as the embed API; and
• With your prior approval, use selected content in other formats (for example, image uploads or reposts).

Ownership of your posts remains fully with you.
We do not claim any ownership rights over your posts or likeness. Any use of your content under this agreement is limited, revocable, and solely for the purposes described above.
This usage is limited to:
• Cloutfit.ai, OutfitAlliance.com, and associated subdomains; and
• Partner brand websites, solely for the purpose of showcasing your matched outfits and enabling shopping experiences.

3. Earnings and Payment
• You will earn a commission on qualifying sales made through your Cloutfit.ai or OutfitAlliance.com page or tracking links.
• Commission amounts depend on the rates collected from brand partners and may vary.
• Commissions are calculated monthly and paid by the 15th of the following month, once your balance exceeds USD $100 (or the equivalent in your currency).
• Payments are made via Bank Transfer or PayPal.
• You are responsible for any taxes or reporting requirements in your country.

4. Non-Exclusivity
This is a non-exclusive agreement. You are free to collaborate with other brands, platforms, or services at any time. Cloutfit places no restrictions on your current or future partnerships.

5. Term and Termination
• This agreement is effective as soon as you accept it and continues until terminated by either party.
• Either party may terminate this agreement at any time with 7 days' written notice.
• After termination, your creator page will be deactivated and we will stop using your content unless otherwise agreed in writing.

6. Confidentiality and Data
• Any non-public information we share with you should remain confidential.
• Your performance data will be used solely for reporting, payouts, and platform optimization, in line with our Privacy Policy.

7. Independent Relationship
This agreement does not create an employment, agency, or joint venture relationship. You are participating as an independent creator or contractor.

8. Governing Law
This agreement is governed by and interpreted in accordance with the laws of the United Arab Emirates.

By submitting the form below and clicking "I Agree," you confirm that you have read, understood, and accepted these terms.`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-roxborough">Join the program</DialogTitle>
          <DialogDescription>
            Complete the form below to join our Brand Partnership Program and start collaborating with creators.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] pr-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="brandName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Brand Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your brand name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website *</FormLabel>
                      <FormControl>
                        <Input placeholder="https://yourbrand.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactPersonName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Person Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter contact person name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contact@yourbrand.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Phone Number *</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          countryCode={form.watch("contactPhoneCountryCode")}
                          onCountryCodeChange={(code) => form.setValue("contactPhoneCountryCode", code)}
                          placeholder="Contact phone number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="promoCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Promo Code</FormLabel>
                      <FormControl>
                        <Input placeholder="Optional promo code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Shopping Feed Links *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ url: "", language: "", country: "" })}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add more
                  </Button>
                </div>
                
                {fields.map((field, index) => (
                  <div key={field.id} className="space-y-3 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Feed {index + 1}</Label>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <FormField
                      control={form.control}
                      name={`shoppingFeeds.${index}.url`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Feed URL *</FormLabel>
                          <FormControl>
                            <Input placeholder="https://feeds.yourbrand.com/products.xml" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name={`shoppingFeeds.${index}.language`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Language *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {languages.map((lang) => (
                                  <SelectItem key={lang.value} value={lang.value}>
                                    {lang.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`shoppingFeeds.${index}.country`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Country *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select country" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem key={country.value} value={country.value}>
                                    {country.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <Label className="text-base font-semibold">Brand Partnership Agreement</Label>
                <div className="p-4 bg-muted rounded-lg max-h-60 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans">
                    {agreementText}
                  </pre>
                </div>

                <FormField
                  control={form.control}
                  name="agreementAccepted"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm">
                          I have read and agree to the Brand Partnership Agreement *
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "I agree and Join the program"}
              </Button>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default BrandRegistrationDialog;
